"use client";

import { motion } from "motion/react";
import { useEffect, useState, useRef } from "react";
import { BlendedGradient } from "./blended_gradient";
import { useTranslations } from "next-intl";

type SectionType = 'flashcards' | 'mindmaps' | 'tests';

interface VideosWithFeaturesProps {
    section_refs?: {
        flashcards: React.RefObject<HTMLDivElement | null>;
        mindmaps: React.RefObject<HTMLDivElement | null>;
        tests: React.RefObject<HTMLDivElement | null>;
    };
    FEATURE_CONTENT?: Record<SectionType, FeatureContentType>;
}

interface FeatureContentType {
    subtitle: string;
    title: string;
    description: string[];
}

interface AnimatedVideoProps {
    is_active: boolean;
    video_src: string;
    should_load_media: boolean;
    is_first_video?: boolean;
    is_last_video?: boolean;
    has_left_first_video?: boolean;
    has_past_last_video?: boolean;
}

const SECTIONS: SectionType[] = ['flashcards', 'mindmaps', 'tests'];

const VIDEO_SOURCES: Record<SectionType, { src: string; alt: string }> = {
    flashcards: {
        src: '/carousel_recordings/understand.gif',
        alt: 'Student understanding the content'
    },
    mindmaps: {
        src: '/carousel_recordings/retain.gif',
        alt: 'Student using Spaced Repetition to retain the content'
    },
    tests: {
        src: '/carousel_recordings/apply.gif',
        alt: 'Student applying the content via tests'
    }
};

const ANIMATION_CONFIG = {
    duration: 0.4,
    ease: [0.34, 1.56, 0.64, 1] as const,
    type: "spring" as const,
    stiffness: 300,
    damping: 25,
    exit_position: { x: 120, y: 120 },
    exit_scale: 0.6
} as const;

function throttle(func: Function, limit: number) {
    let in_throttle: boolean;
    return function(this: any, ...args: any[]) {
        if (!in_throttle) {
            func.apply(this, args);
            in_throttle = true;
            setTimeout(() => in_throttle = false, limit);
        }
    };
}

function AnimatedVideo({ 
    is_active, 
    video_src, 
    should_load_media,
    is_first_video = false, 
    is_last_video = false,
    has_left_first_video = false,
    has_past_last_video = false 
}: AnimatedVideoProps) {
    let opacity, x, y, scale, duration;

    if (is_first_video && !has_left_first_video) {
        opacity = 1; x = 0; y = 0; scale = 1; duration = 0;
    } else if (is_last_video && has_past_last_video) {
        opacity = 1; x = 0; y = 0; scale = 1; duration = is_active ? ANIMATION_CONFIG.duration : 0;
    } else {
        opacity = is_active ? 1 : 0;
        x = is_active ? 0 : ANIMATION_CONFIG.exit_position.x;
        y = is_active ? 0 : ANIMATION_CONFIG.exit_position.y;
        scale = is_active ? 1 : ANIMATION_CONFIG.exit_scale;
        duration = ANIMATION_CONFIG.duration;
    }

    const isVideo = video_src.endsWith('.mp4') || video_src.endsWith('.webm') || video_src.endsWith('.mov');

    return (
        <motion.div
            initial={{ opacity: is_first_video ? 1 : 0, x: 0, y: 0, scale: 1 }}
            animate={{ opacity, x, y, scale }}
            transition={{ 
                duration,
                ease: ANIMATION_CONFIG.ease,
                type: ANIMATION_CONFIG.type,
                stiffness: ANIMATION_CONFIG.stiffness,
                damping: ANIMATION_CONFIG.damping
            }}
            className="absolute inset-0 aspect-18/9 rounded-2xl overflow-hidden shadow-2xl"
            style={{ 
                zIndex: is_active ? 2 : 1,
                pointerEvents: is_active ? 'auto' : 'none'
            }}
        >
            {/* Canvas background */}
            <img 
                src="/canvas_background.png" 
                alt="Canvas background"
                className="absolute inset-0 w-full h-full object-contain z-0"
                loading="lazy"
                decoding="async"
                fetchPriority="low"
            />
            {/* Video or image content */}
            <div className="absolute bottom-0 right-0 w-[85%] h-[90%] z-10">
                {!should_load_media ? null : isVideo ? (
                    <video
                        className="w-full h-full object-contain"
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                    >
                        <source src={video_src} type="video/mp4" />
                    </video>
                ) : (
                    <img
                        src={video_src}
                        alt=""
                        className="w-full h-full object-contain"
                        loading="lazy"
                        decoding="async"
                        fetchPriority="low"
                    />
                )}
            </div>
        </motion.div>
    );
}

function FeatureSection({ 
    section, 
    section_ref, 
    content 
}: { 
    section: SectionType;
    section_ref?: React.RefObject<HTMLDivElement | null>;
    content: FeatureContentType;
}) {
    const spacing = section === 'flashcards' ? 'pt-12 mt-[-300px]' : section === 'tests' ? '' : 'pt-64';
    const center_sticky_class = section === 'tests' ? 'sticky top-1/2 -translate-y-1/2' : '';
    const padding = section === 'tests' ? 'px-3 py-64 rounded-lg' : section === 'mindmaps' ? 'p-6 rounded-lg' : '';
    const subtitleMargin = section === 'tests' ? 'mb-0' : 'mb-4';
    const titleMargin = section === 'tests' ? 'mb-1' : 'mb-8';
    const descriptionSpacing = section === 'tests' ? 'space-y-1' : 'space-y-6';
    const flexCenter = section === 'tests' ? 'flex flex-col justify-center' : '';
    
    return (
        <motion.div
            ref={section_ref}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: false, margin: "-100px" }}
            className={`${spacing} ${center_sticky_class} ${padding} ${flexCenter}`}
        >
            <p className={`${subtitleMargin} text-gray-600`}>{content.subtitle}</p>
            <h2 className={`${titleMargin} text-2xl font-bold text-gray-900`}>{content.title}</h2>
            <div className={`${descriptionSpacing} text-gray-600`}>
                {content.description.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                ))}
            </div>
        </motion.div>
    );
}

function ContentCard({ 
    content, 
    section_ref 
}: { 
    content: FeatureContentType;
    section_ref?: React.RefObject<HTMLDivElement | null>;
}) {
    return (
        <motion.div
            ref={section_ref}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            viewport={{ once: false, margin: "-100px" }}
            className="p-6"
        >
            <p className="text-sm font-medium text-blue-600 mb-3">
                {content.subtitle}
            </p>
            <h2 className="text-xl font-bold text-gray-900 mb-4 leading-tight">
                {content.title}
            </h2>
            <div className="space-y-4">
                {content.description.map((paragraph, index) => (
                    <p 
                        key={index}
                        className="text-gray-600 text-sm leading-relaxed"
                    >
                        {paragraph}
                    </p>
                ))}
            </div>
        </motion.div>
    );
}

function MobileScrollBased({ FEATURE_CONTENT, section_refs, should_load_media }: VideosWithFeaturesProps & { FEATURE_CONTENT: Record<SectionType, FeatureContentType>; should_load_media: boolean }) {
    const [active_section, set_active_section] = useState<SectionType>('flashcards');
    const container_ref = useRef<HTMLDivElement>(null);

    const MOBILE_SEGMENTS = {
        first_end: 0.3,
        second_start: 0.35,
        second_end: 0.65,
        third_start: 0.7
    } as const;

    useEffect(() => {
        const handle_scroll = () => {
            if (!container_ref.current) return;

            const container = container_ref.current;
            const container_rect = container.getBoundingClientRect();
            const scroll_top = Math.max(0, -container_rect.top);
            const scroll_height = Math.max(1, container_rect.height - window.innerHeight);
            const progress = Math.min(1, Math.max(0, scroll_top / scroll_height));

            let new_active_section: SectionType = 'flashcards';
            if (progress < MOBILE_SEGMENTS.first_end) {
                new_active_section = 'flashcards';
            } else if (progress >= MOBILE_SEGMENTS.second_start && progress < MOBILE_SEGMENTS.second_end) {
                new_active_section = 'mindmaps';
            } else if (progress >= MOBILE_SEGMENTS.third_start) {
                new_active_section = 'tests';
            } else {
                new_active_section = progress < 0.5 ? 'flashcards' : 'mindmaps';
            }

            set_active_section(new_active_section);
        };

        const throttled_scroll = throttle(handle_scroll, 16);
        window.addEventListener('scroll', throttled_scroll, { passive: true });
        handle_scroll();

        return () => window.removeEventListener('scroll', throttled_scroll);
    }, []);

    return (
        <div ref={container_ref} className="md:hidden relative">
            <div className="sticky top-0 pt-2 sm:pt-6 bg-white z-20 mb-6">
                <div className="px-4 pt-2 sm:pt-6">
                    <div className="mx-auto">
                        <motion.div
                            className="relative aspect-video rounded-2xl overflow-hidden shadow bg-white"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                        >
                            {should_load_media && SECTIONS.map((section) => {
                                const is_active = active_section === section;
                                
                                return (
                                    <motion.img
                                        key={section}
                                        src={VIDEO_SOURCES[section].src}
                                        alt={VIDEO_SOURCES[section].alt}
                                        className="absolute inset-0 w-full h-full object-cover"
                                        initial={{ opacity: 0, scale: 1.05 }}
                                        animate={{ 
                                            opacity: is_active ? 1 : 0,
                                            scale: is_active ? 1 : 1.05
                                        }}
                                        transition={{ 
                                            duration: 0.5, 
                                            ease: "easeInOut",
                                            opacity: { duration: 0.3 }
                                        }}
                                        style={{ 
                                            zIndex: is_active ? 10 : 1,
                                            pointerEvents: is_active ? 'auto' : 'none'
                                        }}
                                        loading="lazy"
                                        decoding="async"
                                        fetchPriority="low"
                                    />
                                );
                            })}
                            
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                                <div className="flex space-x-1">
                                    {SECTIONS.map((section, index) => (
                                        <div
                                            key={index}
                                            className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                                active_section === section 
                                                ? 'bg-white shadow-lg scale-125' 
                                                : 'bg-white/50'
                                            }`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>

            <div className="px-4">
                <div className="space-y-32 pb-32">
                    {SECTIONS.map((section, index) => {
                        const content = FEATURE_CONTENT[section];
                        const spacing = index === 0 ? 'pt-24' : 'pt-32';
                        
                        return (
                            <div key={section} className={`mx-auto ${spacing}`}>
                                <ContentCard 
                                    content={content}
                                    section_ref={section_refs?.[section]}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export function VideosWithFeatures({ section_refs }: VideosWithFeaturesProps) {
    const t = useTranslations();
    
    const [active_section, set_active_section] = useState<SectionType | null>('flashcards');
    const [scroll_progress, set_scroll_progress] = useState(0);
    const [has_left_first_video, set_has_left_first_video] = useState(false);
    const [has_past_last_video, set_has_past_last_video] = useState(false);
    const [should_load_media, set_should_load_media] = useState(false);
    const shell_ref = useRef<HTMLDivElement>(null);
    const container_ref = useRef<HTMLDivElement>(null);
    const last_progress_ref = useRef<number>(0);

    const FEATURE_CONTENT: Record<SectionType, FeatureContentType> = {
        flashcards: {
            subtitle: "AI with guardrails",
            title: "Secure, responsible AI with guardrails",
            description: [
                `Unlike generic AI tools, StudyStash AI is exclusively grounded in your ${t("general.words.lecturers")}' original materials, ensuring accurate and relevant learning aligned with course objectives.`,
                // "Our intelligent verification system cross-references student inputs against course materials to catch potential errors or misconceptions, protecting academic integrity."
            ]
        },
        mindmaps: {
            subtitle: "For when generic AI is not enough",
            title: "Adaptive learning intelligence built on proven neuroscience",
            description: [
                "StudyStash is redefining what effective learning looks like; embedding proven cognitive learning science into every interaction which adapts to each student's level, pace, and learning style."
            ]
        },
        tests: {
            subtitle: "Reduced faculty workload",
            title: "Less work, more learning",
            description: [
                "StudyChat responds to student queries, produces and marks formative assessments, and delivers personalised feedback — all informed by each learner’s areas of difficulty.",
            ]
        }
    };

    const SEGMENTS = {
        first_end: 0.04,
        gap1_end: 0.23,
        second_end: 0.41,
        gap2_end: 0.56
    } as const;

    useEffect(() => {
        if (should_load_media) return;
        const node = shell_ref.current;
        if (!node) return;
        if (!("IntersectionObserver" in window)) {
            set_should_load_media(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry?.isIntersecting) return;
                set_should_load_media(true);
                observer.disconnect();
            },
            { rootMargin: "900px 0px" }
        );

        observer.observe(node);

        return () => observer.disconnect();
    }, [should_load_media]);

    useEffect(() => {
        const handle_scroll = () => {
            if (!container_ref.current) return;

            const container = container_ref.current;
            const container_rect = container.getBoundingClientRect();
            const scroll_top = Math.max(0, -container_rect.top);
            const scroll_height = Math.max(1, container_rect.height - window.innerHeight);
            const progress = Math.min(1, Math.max(0, scroll_top / scroll_height));
            set_scroll_progress(progress);

            const scrolling_down = progress >= last_progress_ref.current;
            last_progress_ref.current = progress;

            let new_active_section: SectionType | null = null;
            if (progress < SEGMENTS.first_end) {
                new_active_section = 'flashcards';
            } else if (progress < SEGMENTS.gap1_end) {
                new_active_section = null;
            } else if (progress < SEGMENTS.second_end) {
                new_active_section = 'mindmaps';
            } else if (progress < SEGMENTS.gap2_end) {
                new_active_section = null;
            } else {
                new_active_section = 'tests';
            }

            if (!has_left_first_video && progress >= SEGMENTS.first_end) {
                set_has_left_first_video(true);
            }

            if (scrolling_down && progress >= SEGMENTS.gap2_end) {
                set_has_past_last_video(true);
            }
            if (!scrolling_down && progress < SEGMENTS.gap2_end - 0.02) {
                set_has_past_last_video(false);
            }

            if (progress === 0) {
                set_has_left_first_video(false);
                set_has_past_last_video(false);
            }

            set_active_section(new_active_section);
        };

        window.addEventListener('scroll', handle_scroll);
        handle_scroll();

        return () => window.removeEventListener('scroll', handle_scroll);
    }, [section_refs, active_section, has_left_first_video]);

    return (
        <div ref={shell_ref}>
            <MobileScrollBased FEATURE_CONTENT={FEATURE_CONTENT} section_refs={section_refs} should_load_media={should_load_media} />
            
            <div ref={container_ref} className="relative min-h-screen hidden md:block">
                <div className="container mx-auto px-6 lg:px-8">
                    <div className="grid grid-cols-[1fr_1.5fr] gap-12 items-start min-h-screen">
                        <div className="sticky top-8 pt-24 lg:pt-32">
                            <div className="space-y-64 pb-108 mr-10">
                                {SECTIONS.map((section) => (
                                    <FeatureSection 
                                        key={section}
                                        section={section}
                                        section_ref={section_refs?.[section]}
                                        content={FEATURE_CONTENT[section]}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="block sticky top-1/2 -translate-y-1/2 overflow-visible">
                            <BlendedGradient
                                activeSection={active_section}
                                scrollProgress={scroll_progress}
                                className="relative w-full max-w-[700px] aspect-square rounded-full rounded-br-none ml-auto translate-x-[calc((100vw-100%)/15)] p-6 md:p-10 flex items-center justify-center overflow-visible"
                            >
                                <div className="absolute inset-0 opacity-40 mix-blend-multiply pointer-events-none"></div>
                                
                                <div className="w-full h-full flex items-center justify-center relative z-10">
                                    <div className="aspect-video w-full relative scale-[1.25] right-20">
                                        {SECTIONS.map((section, index) => (
                                            <AnimatedVideo
                                                key={section}
                                                is_active={active_section === section}
                                                video_src={VIDEO_SOURCES[section].src}
                                                should_load_media={should_load_media}
                                                is_first_video={index === 0}
                                                is_last_video={index === SECTIONS.length - 1}
                                                has_left_first_video={has_left_first_video}
                                                has_past_last_video={has_past_last_video}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </BlendedGradient>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
